// New design tables (.ui-tw scrollers): row action bars that no overflow can clip, and columns that make way before
// the table scrolls sideways. Imported for its side effect by every New design entry; installing twice is harmless.
//
// Row action bars. A row's bar (copy link, open on TikTok, remove…) is <span data-rowtray popover="manual"> in its
// last cell. It used to sit absolutely inside that cell, so the table's scroller (.ui-tcard .ui-tw, overflow-x:auto)
// cut it off whenever the last column was scrolled out of view. Now it opens in the browser's top layer when the row
// is hovered or anything in it has focus, fixed to the row's middle and to the right edge of the table's visible part
// (left of the row's own ⋯ / bookmark button when that is in view), and follows page and table scrolls and resizes.
// It stays in the row in the DOM, so Tab walks into it in order, hover counts as the row's, and Livewire morphs it
// like any other cell. Escape closes it. Phones (cards, swipe) never show it: their CSS hides the bar.
//
// Column priority, for embedded / showcase tables only (the homepage's Trending window), which must never scroll
// sideways. Dashboard tables keep every column and scroll sideways on tablets and desktops (phones get cards); don't
// give them data-pri. A <th data-pri="1|2|3…"> column may be dropped when the table is too wide for its scroller:
// the lowest number goes first, then the next, until scrollWidth <= clientWidth (or nothing is left to drop, when
// the table scrolls sideways as a last resort). Cells of a dropped column get [data-fit-off] (display:none) and a cell
// spanning it gets a smaller colspan. It runs before paint on resize and whenever Livewire changes the rows, and only
// while the table is laid out as a table (not as phone cards).

import { uiFloat, uiUnfloat } from './ui-float';

const TRAY = '[data-rowtray]';
const EDGE = 8;

function installTrays() {
    if (window.__uiRowTray) return;
    window.__uiRowTray = true;
    let cur = null; // { row, tray }
    let hideT = 0;
    let frame = 0;

    const trayOf = (row) => row?.querySelector(TRAY) ?? null;
    const rowOf = (el) => (el instanceof Element ? el.closest('tr') : null);
    // a table laid out as a table (phones lay rows out as cards and hide the bar)
    const usable = (tray) => !!tray && tray.isConnected && !tray.closest('[data-fit-off]') && getComputedStyle(tray.closest('table') ?? tray).display === 'table';

    const place = () => {
        if (!cur) return;
        const { row, tray } = cur;
        if (!row.isConnected || !tray.isConnected) { cur = null; return; }
        const r = row.getBoundingClientRect();
        const box = (row.closest('.ui-tw') ?? row).getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        // the right edge of what the reader sees of the table
        let right = Math.min(box.right, vw) - EDGE;
        // sit left of the row's own button (⋯, bookmark, Open) when it is in view
        const own = tray.nextElementSibling;
        if (own && own.offsetWidth) {
            const o = own.getBoundingClientRect();
            if (o.right <= box.right + 1 && o.left >= box.left) right = o.left - 4;
        }
        const w = tray.offsetWidth;
        const h = tray.offsetHeight;
        const left = Math.max(Math.max(box.left, 0) + EDGE, right - w);
        tray.style.left = `${Math.round(left)}px`;
        tray.style.top = `${Math.round(r.top + r.height / 2 - h / 2)}px`;
    };
    const follow = () => { if (cur && !frame) frame = requestAnimationFrame(() => { frame = 0; place(); }); };

    const open = (row) => {
        clearTimeout(hideT);
        const tray = trayOf(row);
        if (!usable(tray)) return;
        if (cur && cur.tray === tray) return place();
        close();
        cur = { row, tray };
        uiFloat(tray);
        if (getComputedStyle(tray).display === 'none') { cur = null; uiUnfloat(tray); return; }
        place();
    };
    function close() {
        clearTimeout(hideT);
        if (!cur) return;
        const { tray } = cur;
        cur = null;
        if (tray.contains(document.activeElement)) tray.nextElementSibling?.focus?.();
        uiUnfloat(tray);
    }
    const later = () => { clearTimeout(hideT); hideT = setTimeout(close, 60); };

    document.addEventListener('pointerover', (e) => {
        if (e.pointerType === 'touch') return;
        const row = rowOf(e.target);
        if (row && trayOf(row)) open(row);
    });
    document.addEventListener('pointerout', (e) => {
        if (!cur || e.pointerType === 'touch') return;
        const to = e.relatedTarget;
        if (to instanceof Node && cur.row.contains(to)) return;
        if (cur.row.contains(e.target) && !cur.row.contains(document.activeElement)) later();
    });
    document.addEventListener('focusin', (e) => {
        const row = rowOf(e.target);
        if (row && trayOf(row)) open(row);
        else if (cur && !cur.row.contains(e.target)) close();
    });
    document.addEventListener('focusout', (e) => {
        if (!cur || !cur.row.contains(e.target)) return;
        const to = e.relatedTarget;
        if (to instanceof Node && cur.row.contains(to)) return;
        if (!cur.row.matches(':hover')) later();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && cur) close(); });
    window.addEventListener('scroll', follow, { passive: true, capture: true });
    window.addEventListener('resize', follow);
    document.addEventListener('livewire:navigated', close);
    // Livewire may swap the row (sort, page, remove) under an open bar
    new MutationObserver(() => { if (cur && (!cur.row.isConnected || !cur.tray.isConnected || !usable(cur.tray))) { const t = cur.tray; cur = null; uiUnfloat(t); } })
        .observe(document.documentElement, { childList: true, subtree: true });
}

// ── column priority ─────────────────────────────────────────────────────────

const seen = new WeakSet();
const watchers = new WeakMap();

function priorities(table) {
    const head = table.tHead?.rows[0];
    if (!head) return [];
    const pri = [];
    for (const th of head.cells) {
        const p = Number(th.dataset.pri) || 0;
        const span = Number(th.dataset.fitSpan) || th.colSpan || 1;
        for (let i = 0; i < span; i++) pri.push(p);
    }
    return pri;
}

/** Hide every column whose priority is at or below `level` (0 shows them all). */
function apply(table, pri, level) {
    for (const row of table.rows) {
        let col = 0;
        for (const cell of row.cells) {
            const span = Number(cell.dataset.fitSpan) || cell.colSpan || 1;
            let shown = 0;
            for (let i = 0; i < span; i++) if (!(pri[col + i] && pri[col + i] <= level)) shown++;
            col += span;
            if (span > 1) {
                if (shown !== span && shown > 0) { cell.dataset.fitSpan = span; cell.colSpan = shown; }
                else if (cell.dataset.fitSpan) { cell.colSpan = span; delete cell.dataset.fitSpan; }
            }
            if (shown === 0) cell.setAttribute('data-fit-off', '');
            else if (cell.hasAttribute('data-fit-off')) cell.removeAttribute('data-fit-off');
        }
    }
}

function fit(tw) {
    const table = tw.querySelector(':scope > table');
    if (!table) return;
    const pri = priorities(table);
    const levels = [...new Set(pri.filter(Boolean))].sort((a, b) => a - b);
    if (!levels.length) return edge(tw);
    // Phone cards (display:block) and hidden tables keep every cell: their CSS picks what shows.
    const asTable = getComputedStyle(table).display === 'table' && tw.clientWidth > 0;
    const fits = () => tw.scrollWidth <= tw.clientWidth + 1;
    apply(table, pri, 0);
    if (asTable && !fits()) {
        // the fewest columns to drop: binary search over the levels (each step is one layout)
        let lo = 0, hi = levels.length - 1, best = hi;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            apply(table, pri, levels[mid]);
            if (fits()) { best = mid; hi = mid - 1; } else lo = mid + 1;
        }
        apply(table, pri, levels[best]);
    }
    edge(tw);
}

/** [data-overflow]: more of the table to the right, so the pinned actions column casts a shadow over it. */
function edge(tw) {
    const more = tw.scrollLeft + tw.clientWidth < tw.scrollWidth - 1;
    if ((tw.dataset.overflow === 'true') !== more) tw.dataset.overflow = more;
    watchers.get(tw)?.mo.takeRecords(); // our own changes are not news
}

function watch(tw) {
    if (seen.has(tw)) return;
    seen.add(tw);
    let frame = 0;
    const soon = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; if (tw.isConnected) fit(tw); }); };
    const ro = new ResizeObserver(soon);
    ro.observe(tw);
    // Livewire morphs rows in and puts the server's colspans back (and drops our attributes): fit again
    const mo = new MutationObserver(soon);
    mo.observe(tw, { childList: true, subtree: true, attributes: true, attributeFilter: ['colspan', 'data-fit-off', 'data-pri', 'data-overflow'] });
    watchers.set(tw, { ro, mo });
    fit(tw);
}

function installFit() {
    if (window.__uiFit) return;
    window.__uiFit = true;
    let frame = 0;
    const scan = () => {
        frame = 0;
        document.querySelectorAll('.ui-tw').forEach((tw) => { if (!seen.has(tw) && tw.querySelector(':scope > table')) watch(tw); });
    };
    scan();
    document.addEventListener('scroll', (e) => { if (e.target instanceof Element && e.target.matches('.ui-tw') && seen.has(e.target)) edge(e.target); }, { passive: true, capture: true });
    new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(scan); }).observe(document.documentElement, { childList: true, subtree: true });
    // web fonts change widths
    document.fonts?.ready.then(() => document.querySelectorAll('.ui-tw').forEach((tw) => seen.has(tw) && fit(tw)));
}

if (typeof window !== 'undefined') {
    installTrays();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', installFit, { once: true });
    else installFit();
}
